import React, { useRef } from "react";
import {
    Code2,
    Bold,
    Highlighter,
    Plus,
    Trash2,
} from "lucide-react";

const LANGUAGES = [
    "python",
    "javascript",
    "java",
    "cpp",
    "c",
    "csharp",
    "go",
    "rust",
    "php",
];

export default function BlockEditorPanel({
                                             fragmentId,
                                             block,
                                             onChange,
                                             onOpenAdvancedJson,
                                         }) {
    if (!fragmentId)
        return <div className="text-gray-500">یک فرگمنت انتخاب کن</div>;
    if (!block) return <div className="text-gray-500">یک بلاک انتخاب کن</div>;

    const set = (patch) => onChange(patch);
    const textRef = useRef(null);

    const wrapSelection = (tag) => {
        const el = textRef.current;
        if (!el) return;
        const { selectionStart, selectionEnd, value } = el;
        if (selectionStart === selectionEnd) return;

        const wrapped =
            value.slice(0, selectionStart) +
            `<${tag}>` +
            value.slice(selectionStart, selectionEnd) +
            `</${tag}>` +
            value.slice(selectionEnd);

        set({ config: { text: wrapped } });
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold">ویرایش بلاک</h2>
                <button
                    onClick={onOpenAdvancedJson}
                    className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-50 text-sm flex items-center gap-2"
                >
                    <Code2 size={16} /> JSON
                </button>
            </div>

            {/* ================= TEXT ================= */}
            {block.type === "text" && (
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <button onClick={() => wrapSelection("b")} className="p-2 border rounded">
                            <Bold size={16} />
                        </button>
                        <button onClick={() => wrapSelection("h")} className="p-2 border rounded">
                            <Highlighter size={16} />
                        </button>
                    </div>

                    <textarea
                        ref={textRef}
                        value={block.config.text}
                        onChange={(e) => set({ config: { text: e.target.value } })}
                        rows={8}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    />
                </div>
            )}

            {/* ================= HEADING ================= */}
            {block.type === "heading" && (
                <div className="space-y-2">
                    <input
                        value={block.config.text}
                        onChange={(e) => set({ config: { ...block.config, text: e.target.value } })}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                        placeholder="عنوان"
                    />

                    <select
                        value={block.config.level}
                        onChange={(e) => set({ config: { ...block.config, level: Number(e.target.value) } })}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    >
                        {[1,2,3,4,5,6].map(l => (
                            <option key={l} value={l}>H{l}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* ================= IMAGE ================= */}
            {block.type === "image" && (
                <div className="space-y-2">
                    <input
                        placeholder="Image URL"
                        value={block.config.src}
                        onChange={(e) => set({ config: { ...block.config, src: e.target.value } })}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    />
                    <input
                        placeholder="Alt text"
                        value={block.config.alt}
                        onChange={(e) => set({ config: { ...block.config, alt: e.target.value } })}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    />
                    <input
                        placeholder="Caption"
                        value={block.config.caption}
                        onChange={(e) => set({ config: { ...block.config, caption: e.target.value } })}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    />
                </div>
            )}

            {/* ================= CODE ================= */}
            {block.type === "code" && (
                <div className="space-y-3">
                    <select
                        value={block.config.language}
                        onChange={(e) => set({ config: { ...block.config, language: e.target.value } })}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    >
                        {LANGUAGES.map((l) => (
                            <option key={l} value={l}>{l}</option>
                        ))}
                    </select>

                    <textarea
                        value={block.config.code}
                        onChange={(e) => set({ config: { ...block.config, code: e.target.value } })}
                        rows={6}
                        className="w-full p-3 border rounded-lg bg-gray-50 font-mono"
                    />

                    <label className="flex gap-2 items-center">
                        <input
                            type="checkbox"
                            checked={block.config.editable}
                            onChange={(e) => set({ config: { ...block.config, editable: e.target.checked } })}
                        />
                        قابل ویرایش
                    </label>

                    <label className="flex gap-2 items-center">
                        <input
                            type="checkbox"
                            checked={block.config.showCompiler}
                            onChange={(e) => set({ config: { ...block.config, showCompiler: e.target.checked } })}
                        />
                        نمایش کامپایلر
                    </label>

                    <textarea
                        placeholder="نتیجه مورد انتظار"
                        value={block.config.result.expected}
                        onChange={(e) =>
                            set({
                                config: {
                                    ...block.config,
                                    result: { ...block.config.result, expected: e.target.value },
                                },
                            })
                        }
                        className="w-full p-3 border rounded-lg bg-gray-50 font-mono"
                        rows={3}
                    />

                    <input
                        placeholder="هینت"
                        value={block.config.hint}
                        onChange={(e) => set({ config: { ...block.config, hint: e.target.value } })}
                        className="w-full p-3 border rounded-lg bg-gray-50"
                    />
                </div>
            )}
        </div>
    );
}
